// Phase 10 /faq — Counter-UAS simulation FAQ.
//
// Primary keywords: UAVSimNet FAQ, counter-UAS simulation questions.
// Emits FAQPage schema (Google's rich-result surface for FAQ content)
// plus Organization + BreadcrumbList.

import { Link } from "react-router-dom";
import SeoHead from "@/components/SeoHead";
import {
    buildBreadcrumbListSchema,
    buildFaqPageSchema,
} from "@/lib/schema";

const FAQ = [
    {
        question: "What is UAVSimNet?",
        answer:
            "UAVSimNet is a counter-UAS simulation platform for defence procurement, engineering, and operator rehearsal. It runs high-fidelity interceptor flight dynamics, Monte Carlo engagement analyses, and produces evidence-grade reports that survive a procurement review.",
    },
    {
        question: "Who is UAVSimNet for?",
        answer:
            "Three teams: defence programme offices evaluating counter-UAS interceptors; engineering teams calibrating interceptor models against real telemetry; and operator cells rehearsing missions before a live-fire window. UAVSimNet is not a consumer tool and is not sold to unlicensed industry.",
    },
    {
        question: "What flight-dynamics backend does it use?",
        answer:
            "Two options. A kinematic backend that models pursuit engagements deterministically and runs fast — ideal for large Monte Carlo sweeps and for regression replay. A JSBSim hybrid backend that drives a 6-DoF aero model where higher fidelity is needed. Scenario compilation picks the right backend based on what the operator requested and on envelope validity.",
    },
    {
        question: "How many Monte Carlo samples should I run?",
        answer:
            "It depends on the question. Sanity sweeps use 8 to 24 samples. Trade studies and sizing runs typically use 60 to 500. Calibration-validation runs with paired before/after metrics run 200 or more to tighten the confidence intervals. The platform exposes samples directly via an optional_overrides field so operators don't need to rebuild a scenario to change the count.",
    },
    {
        question: "Does UAVSimNet produce procurement-ready evidence?",
        answer:
            "Yes. Every run produces a compiled scenario (YAML), a numerical record (JSON plus CSV), and a structured decision document (PDF and DOCX with embedded plots). Runs carry immutable provenance — every model version, every seed, every input hash is stamped in the manifest, so a reviewer can re-open the record months later and reproduce the numbers.",
    },
    {
        question: "What's the credibility story?",
        answer:
            "Five things. A versioned model registry with validity envelopes. A regression harness that compares new runs against blessed golden baselines. Calibration sessions with audit-trail acceptance. Replay determinism so any reviewer can independently re-run your numbers. And structured reports produced directly from the run, not assembled by hand.",
    },
    {
        question: "Where are you hosted, and what's the security posture?",
        answer:
            "UAVSimNet is deployed as a multi-tenant SaaS with per-customer tenant isolation, invite-only authenticated access, single-use invite links, signed audit trails, and export-control alignment for defence and licensed-industry use. The /security page carries the full posture statement.",
    },
    {
        question: "How do I get access?",
        answer:
            "Access is by referral or via the access-request form on /contact. Onboarding — including tenant provisioning, invite-link issue, and a walk-through of the operator console — is typically complete within five business days of an approved request.",
    },
    {
        question: "Can I import real flight telemetry?",
        answer:
            "Yes. The platform supports PX4 ULog, ArduPilot .bin, MAVLink tlog, and canonical CSV telemetry imports. Imported logs can drive calibration sessions against registered interceptor models, and imported logs can be compared to a UAVSimNet run through the run-to-log comparison view.",
    },
    {
        question: "Who builds UAVSimNet?",
        answer:
            "UAVSimNet is built by The RLK Group, a UK-registered defence engineering consultancy (Companies House number SC781391, registered in Scotland). See the About page for the full context.",
    },
];

export default function FaqPage() {
    return (
        <>
            <SeoHead
                path="/faq"
                schemaJsonLd={[
                    buildFaqPageSchema(FAQ),
                    buildBreadcrumbListSchema({ path: "/faq", label: "FAQ" }),
                ]}
            />
            <div className="legal-doc" data-testid="faq-page">
                <nav className="legal-doc__breadcrumbs" aria-label="Breadcrumbs">
                    <Link to="/">Home</Link>
                    <span aria-hidden>›</span>
                    <span>FAQ</span>
                </nav>

                <header>
                    <h1 className="legal-doc__title" data-testid="faq-h1">
                        Frequently asked questions
                    </h1>
                    <p className="legal-doc__intro">
                        Short answers to the questions we get most often from procurement
                        teams, engineering teams, and operators evaluating UAVSimNet.
                    </p>
                </header>

                {FAQ.map((qa, i) => (
                    <section
                        key={i}
                        className="legal-doc__section"
                        data-testid={`faq-item-${i}`}
                    >
                        <h2 data-testid={`faq-question-${i}`}>{qa.question}</h2>
                        <p data-testid={`faq-answer-${i}`}>{qa.answer}</p>
                    </section>
                ))}

                <div className="legal-doc__crosslinks">
                    <Link to="/features">Counter-UAS simulation features</Link>
                    <Link to="/evidence">Evidence-grade credibility framework</Link>
                    <Link to="/security">Security posture</Link>
                    <Link to="/contact">Request access</Link>
                </div>
            </div>
        </>
    );
}
